import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form, Alert } from 'react-bootstrap';
import { Code, Search } from 'lucide-react';
import { API_ENDPOINTS } from '../../../config/api.config';
import {apiService} from "../../../services/apiService.ts";

interface ApiSource {
    id: string;
    name: string;
    description?: string;
    configuration: {
        url: string;
        method: string;
        headers?: string;
        body?: string;
        endpoint?: string;
    };
}

interface ApiSelectionModalProps {
    show: boolean;
    onHide: () => void;
    onSelect: (selectedApis: Array<{ id: string }>) => void;
    currentSelected: Array<{ id: string }>;
}

const ApiSelectionModal: React.FC<ApiSelectionModalProps> = ({
                                                                 show,
                                                                 onHide,
                                                                 onSelect,
                                                                 currentSelected
                                                             }) => {
    const [apiSources, setApiSources] = useState<ApiSource[]>([]);
    const [selectedApis, setSelectedApis] = useState(
        new Set(currentSelected.map(api => api.id))
    );
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchApiSources = async () => {
        try {
            setIsLoading(true);
            const data = await apiService.get<{ apiSources: ApiSource[] }>(API_ENDPOINTS.DATA_SOURCE.API_SOURCES.LIST);
            setApiSources(data.apiSources || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch API endpoints');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            fetchApiSources();
            setSelectedApis(new Set(currentSelected.map(api => api.id)));
        }
    }, [show, currentSelected]);

    const handleApiSelection = (apiSource: ApiSource) => {
        setSelectedApis(prev => {
            const newSet = new Set(prev);
            if (newSet.has(apiSource.id)) {
                newSet.delete(apiSource.id);
            } else {
                newSet.add(apiSource.id);
            }
            return newSet;
        });
    };

    const handleSave = () => {
        const selectedSources = Array.from(selectedApis).map(id => ({ id }));
        onSelect(selectedSources);
        onHide();
    };

    const filteredApiSources = apiSources.filter(source =>
        source.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (source.configuration.endpoint || source.configuration.url || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (source.description?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Select API Endpoints</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="mb-3">
                        {error}
                    </Alert>
                )}

                <Form.Group className="mb-3">
                    <div className="position-relative">
                        <div className="position-absolute top-50 translate-middle-y ps-3">
                            <Search size={16} className="text-muted" />
                        </div>
                        <Form.Control
                            type="text"
                            placeholder="Search API endpoints..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="ps-5 border"
                            style={{ height: '38px' }}
                        />
                    </div>
                </Form.Group>

                {isLoading ? (
                    <div className="text-center p-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table hover>
                            <thead>
                            <tr>
                                <th style={{ width: '30px' }}></th>
                                <th>Name</th>
                                <th style={{ width: '100px' }}>Method</th>
                                <th>URL</th>
                                <th style={{ width: '100px' }}>Headers</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredApiSources.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center text-muted py-4">
                                        <Code size={24} className="mb-2" />
                                        <div>No API endpoints found</div>
                                    </td>
                                </tr>
                            ) : (
                                filteredApiSources.map((source) => {
                                    const displayUrl = source.configuration.endpoint || source.configuration.url;
                                    return (
                                        <tr
                                            key={source.id}
                                            onClick={() => handleApiSelection(source)}
                                            className={selectedApis.has(source.id) ? 'table-primary' : ''}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedApis.has(source.id)}
                                                    onChange={() => handleApiSelection(source)}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <Code size={16} className="me-2 text-muted" />
                                                    {source.name}
                                                </div>
                                            </td>
                                            <td>
                                                    <span className="badge bg-secondary bg-opacity-10 text-secondary">
                                                        {source.configuration.method || 'GET'}
                                                    </span>
                                            </td>
                                            <td className="text-muted small">
                                                {displayUrl}
                                            </td>
                                            <td className="text-center">
                                                {source.configuration.headers ? (
                                                    <span className="badge bg-success bg-opacity-10 text-success">Yes</span>
                                                ) : (
                                                    <span className="badge bg-secondary bg-opacity-10 text-secondary">No</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={selectedApis.size === 0}
                >
                    Select ({selectedApis.size}) {selectedApis.size === 1 ? 'Endpoint' : 'Endpoints'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ApiSelectionModal;